<template>
    <div class="flex mr-4 gap-4 items-center text-ink-muted nadia-chat rounded-full w-full px-4 py-4 h-16" data-testid="chat-waiting-message">
        <CoachingModeMarble :is-loading="true" class="-ml-4 max-w-12" />
        <!-- The visible line is keyed by its own text so the cross-fade can run, so it is destroyed
             and recreated on every rotation. A live region has to outlive its own content to announce,
             so the announcement lives here instead: mounted once, written once, and deliberately left
             alone when the message rotates. The rotation is decoration, and announcing it every few
             seconds would be worse than silence. -->
        <p class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ announcement }}</p>
        <div class="relative flex-1">
            <div class="relative h-8 flex items-center gap-2">
                <div class="relative flex-1 h-8 overflow-hidden">
                    <TransitionGroup name="message-fade">
                        <p v-for="(msg, index) in [currentMessage]" :key="`${index}-${msg}`" class="absolute top-0 left-0 right-0 line-clamp-2" aria-hidden="true">
                            {{ msg }}
                        </p>
                    </TransitionGroup>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { onMounted, onUnmounted, ref } from "vue";

const props = defineProps({
    messages: {
        type: Array,
        default: () => [],
    },
    rotationInterval: {
        type: Number,
        default: 3000, // 3 seconds per message
    },
});

const REGION_REGISTRATION_DELAY_MS = 300;

const currentMessage = ref("");
const announcement = ref("");
const rotationTimer = ref(null);

const getRandomMessage = () => {
    if (props.messages.length === 0) {
        return "";
    }
    const randomIndex = Math.floor(Math.random() * props.messages.length);
    return props.messages[randomIndex];
};

const rotateMessage = () => {
    currentMessage.value = getRandomMessage();
};

onMounted(() => {
    // Set initial message
    currentMessage.value = getRandomMessage();

    // A region has to exist before the text it announces, and this one is new on every wait,
    // so the text waits for the reader to register it. 0ms was measured silent on VoiceOver 26
    // with Safari, 300ms announced.
    //
    // The obvious simplification does not work. ChatMessageList already wraps this component in a
    // mounted polite region, and leaning on that was built and listened to: it announces the first
    // wait and then goes quiet from the second one on. A region built per wait announces every
    // time. Why the shared one stops is not established, so do not rewrite this on a theory about
    // it. An isolated page writing the same text into one persistent region does announce both
    // times, so "the reader skips a repeat" is not the explanation.
    setTimeout(() => {
        announcement.value = currentMessage.value;
    }, REGION_REGISTRATION_DELAY_MS);

    // Start rotation if we have messages to rotate
    if (props.messages.length > 1) {
        rotationTimer.value = setInterval(rotateMessage, props.rotationInterval);
    }
});

onUnmounted(() => {
    if (rotationTimer.value) {
        clearInterval(rotationTimer.value);
    }
});
</script>

<style scoped>
.message-fade-enter-active {
    transition: opacity 400ms ease-in-out;
}

.message-fade-leave-active {
    transition: opacity 400ms ease-in-out;
}

.message-fade-enter-from {
    opacity: 0;
}

.message-fade-leave-to {
    opacity: 0;
}
</style>
